import type { clarity } from "clarity-js";
import type { GoogleConsentState } from "./model";
type Clarity = Pick<typeof clarity, "start" | "stop" | "consentv2">;
type Consent = Pick<GoogleConsentState, "analytics_storage" | "ad_storage" | "ad_user_data" | "ad_personalization">;

/** Only the application's committed choices authorize the patched SDK. */
export function createClarityController(environment: {
  load: () => Promise<Clarity>;
  clearStorage: () => void;
  retry?: (attempt: () => void) => void;
}) {
  let analytics = false;
  let advertising = false;
  let requested = false;
  let revoked = false;
  let sdk: Clarity | undefined;
  const allowed = (ads = false) => !revoked && analytics && (!ads || advertising);
  const consent = () => ({
    analytics_Storage: analytics ? "granted" : "denied",
    ad_Storage: advertising ? "granted" : "denied"
  });
  const revoke = () => {
    // A slow reload, pending import, SPA restart or compressed upload cannot revive this document.
    revoked = true;
    try {
      sdk?.stop();
    } catch {
      /* The permission latch also blocks transport if vendor teardown fails. */
    }
    try {
      environment.clearStorage();
    } catch {
      /* Storage failure cannot interrupt Google's consent update. */
    }
  };
  return {
    allowed,
    update(state: Consent) {
      const nextAnalytics = state.analytics_storage === "granted";
      const nextAdvertising = state.ad_storage === "granted" && state.ad_user_data === "granted" && state.ad_personalization === "granted";
      const loss = requested && (analytics && !nextAnalytics || advertising && !nextAdvertising);
      analytics = nextAnalytics;
      advertising = nextAdvertising;
      if (loss) {
        revoke();
      } else if (allowed()) {
        try {
          sdk?.consentv2(consent());
        } catch {
          /* Vendor failures cannot interrupt the canonical consent update. */
        }
      }
    },
    start() {
      if (!allowed() || requested) return false;
      requested = true;
      let attempts = 0;
      const load = () => {
        if (!allowed()) return;
        attempts += 1;
        void environment.load().then(loaded => {
          if (!allowed()) return;
          sdk = loaded;
          sdk.start({
            projectId: "p2r7vpw5kq",
            upload: "https://g.clarity.ms/collect",
            report: "https://report.clarity.ms/sln",
            track: true,
            content: true,
            // Clarity's Measurement purpose does not need to capture Microsoft advertising IDs.
            cookies: [],
            dob: 2498
          });
          sdk.consentv2(consent());
        }).catch(() => {
          if (sdk) revoke();else if (attempts < 2 && allowed()) environment.retry?.(load);
        });
      };
      load();
      return true;
    }
  };
}
function clearClarityStorage() {
  try {
    sessionStorage.removeItem("_cltk");
  } catch {
    /* Storage can be unavailable. */
  }
  const domains = ["", window.location.hostname, `.${window.location.hostname}`];
  const parts = window.location.hostname.split(".");
  for (let index = 1; index < parts.length - 1; index += 1) domains.push(`.${parts.slice(index).join(".")}`);
  for (const name of ["_clck", "_clsk"]) {
    for (const domain of domains) {
      try {
        document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax${domain ? `; Domain=${domain}` : ""}`;
      } catch {
        /* Cookie deletion cannot reauthorize the SDK. */
      }
    }
  }
}
const controller = createClarityController({
  load: async () => (await import("clarity-js")).clarity,
  clearStorage: clearClarityStorage,
  retry: attempt => window.setTimeout(attempt, 1000)
});
declare global {
  interface Window {
    ousterClarityAllowed?: (advertising?: boolean) => boolean;
    ousterStartClarity?: () => boolean;
  }
}
export function installClarityController() {
  window.ousterClarityAllowed = advertising => navigator.globalPrivacyControl !== true && controller.allowed(advertising);
  window.ousterStartClarity = controller.start;
}
export const updateClarityConsent = controller.update;